import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-anger",
  title: "A prayer for anger — before you send the message",
  h1: "A prayer for anger",
  description:
    "A prayer for the ten minutes before you say the thing, four passages that do not tell you anger is a sin, and what to do with the message you drafted.",
  targets: [
    "prayer for anger",
    "prayer to control my temper",
    "prayer when angry at someone",
    "prayer before responding in anger",
  ],
  published: "2026-09-03",
  standfirst:
    "Anger is not the problem the Bible says it is. Speed is. Almost every instruction about it is about how fast, not whether.",
  takeaways: [
    "Ephesians 4:26 quotes a psalm and says be angry — the sin is in what happens next, and specifically in how long it is kept.",
    "James 1:19 is a sequence: hear first, speak second, anger third. Almost all of the damage comes from running it backwards.",
    "Do not send it tonight. Nothing true becomes untrue by morning, and almost everything cruel looks different.",
  ],
  when: [
    "With the message typed and your thumb over send.",
    "After the meeting, in the car, rehearsing what you should have said.",
    "When you are angry with someone who cannot answer back — a child, an employee, a parent who is unwell.",
    "At two in the morning, still going over it.",
  ],
  prayer: [
    "God, I am angry, and some of it is fair. I am not going to start by pretending it is not.",
    "Show me which part is fair and which part is me wanting to win. I cannot tell from in here and I have never been able to.",
    "Stop me from saying the accurate cruel thing. I know exactly what it is. I have had it ready for an hour.",
    "If something needs to be said, let me say it tomorrow, slowly, to the person rather than about them.",
    "And do not let this settle in. I have carried one of these for years before and it cost me more than the original thing did.",
  ],
  short: "God, not tonight. Let me say it tomorrow, slowly, or not at all.",
  honest: [
    "This prayer does not ask for the anger to be removed, and it does not treat being angry as the failure. Several of the passages assume the anger is appropriate and are concerned only with what it does next.",
    "It also does not fix the situation you are angry about. If somebody has genuinely wronged you, praying is not a substitute for the conversation, and swallowing it is not the same as forgiving it.",
    "And it is not advice for a dangerous situation. If the anger in the room is not yours and you are not safe, the right response is to leave and tell somebody, not to pray about your patience.",
  ],
  verses: [
    {
      verse: {
        text: "So, then, my beloved brothers, let every man be swift to hear, slow to speak, and slow to anger; for the anger of man doesn't produce the righteousness of God.",
        ref: "James 1:19–20",
      },
      note: "A sequence with speeds attached, and it is the speeds that carry the instruction rather than any prohibition. Fast at one thing, slow at two others. The reason given in the second half is worth sitting with: not that anger is wicked, but that it does not produce what you think it will — you will not argue anybody into righteousness at volume.",
    },
    {
      verse: {
        text: "\"Be angry, and don't sin.\" Don't let the sun go down on your wrath, and don't give place to the devil.",
        ref: "Ephesians 4:26–27",
      },
      note: "The first clause is a quotation from Psalm 4 and it is an imperative — be angry — which is difficult to reconcile with the assumption that anger is itself the sin. The deadline is the striking part: not a rule about intensity but about duration, and the reason given is that a kept grudge becomes somebody else's foothold.",
    },
    {
      verse: {
        text: "Stand in awe, and don't sin. Search your own heart on your bed, and be still. Selah.",
        ref: "Psalm 4:4",
      },
      note: "The verse Ephesians is quoting, and the instructions attached are physical and specific: go to bed, look at your own part, stop talking. Selah is a pause mark. An ancient text advising an angry person to sleep on it and say nothing until morning is not obviously improvable.",
    },
    {
      verse: {
        text: "A gentle answer turns away wrath, but a harsh word stirs up anger.",
        ref: "Proverbs 15:1",
      },
      note: "A proverb about the second person in the argument, which is where its practical value is: it describes what your reply does to the temperature, not what you deserve to feel. Anyone who has tried it knows the gentle answer is far more expensive than the harsh one, and works far more often.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "The message you drafted",
      id: "the-message-you-drafted",
    },
    {
      type: "p",
      text: "There is a specific modern version of the sun going down on your wrath, and it is a typed message sitting in a box at eleven at night. It is usually accurate. That is what makes it dangerous — accuracy is the thing that lets a cruel sentence feel like honesty.",
    },
    {
      type: "p",
      text: "Write it if writing it helps. Do not send it. Nothing true in it becomes untrue by morning, and the version you send tomorrow will cost you far less.",
    },
    {
      type: "h2",
      text: "Slow is the instruction",
      id: "slow-is-the-instruction",
    },
    {
      type: "p",
      text: "James does not say do not be angry. He says be slow to it, which assumes it is coming and treats the interval as the place where the moral work happens. Everything that makes modern anger worse is an attack on that interval: the reply box, the notification, the audience.",
    },
    {
      type: "note",
      text: "The practical version of slow to anger is almost always a physical delay between the feeling and the sending. Put the phone down. Walk to another room. Anything that costs sixty seconds.",
    },
    {
      type: "h2",
      text: "Anger that has moved in",
      id: "anger-that-has-moved-in",
    },
    {
      type: "p",
      text: "The Ephesians deadline is about the difference between an emotion and a tenant. Anger that is dealt with the same day is an event; anger kept past sundown starts becoming a lens, and after a few months it is simply how you see that person.",
    },
    {
      type: "p",
      text: "If that has already happened, the prayer to say is a different one — closer to {forgiveness}, and it will take longer than an evening.",
      links: {
        forgiveness: "/prayers/prayer-for-forgiveness",
      },
    },
  ],
  faq: [
    {
      q: "Is anger a sin in the Bible?",
      a: "Ephesians 4:26 quotes Psalm 4 as an imperative — be angry, and do not sin — so the anger itself is not treated as the offence. What the passages regulate is speed and duration: how fast it becomes speech, and how long it is kept.",
    },
    {
      q: "What should I pray when I am angry at someone?",
      a: 'Ask for the delay rather than the feeling: "God, not tonight — let me say it tomorrow, slowly, or not at all." Then put the phone somewhere else.',
    },
    {
      q: "What does it mean not to let the sun go down on your anger?",
      a: "It is a deadline rather than a rule about intensity. Anger dealt with the same day stays an event; anger kept becomes a way of seeing the person, which the next verse calls giving the devil a foothold.",
    },
  ],
  relatedPrayers: [
    "prayer-for-forgiveness",
    "prayer-for-patience",
    "prayer-for-my-family",
  ],
  related: [
    "phone-rules-for-christian-families",
    "how-to-stop-scrolling-at-night",
  ],
  relatedTopics: ["bible-verses-about-forgiveness", "bible-verses-about-peace"],
  relatedPassages: ["philippians-4-6-7"],
};
